import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import type { RootState } from '../../reducers';
import { useDocumentationApiFacade } from '../../apis/facades/useDocumentationApiFacade';
import {
  beginDocumentationFetch,
  failDocumentationFetch,
  selectIsOrgFullyFetched,
  setDocumentationData,
  setSaltoObjectTypes,
  setSweepElements,
  setSweepFields,
  setElementDependencies,
} from '../../reducers/documentation/salesforceDocumentationReducer';
import { transformResponse } from '../parser/parserUtils';
import { telemetry } from '../../telemetry';
import {
  addItemsToParserItemsMap,
  generateTagsAndUsersSummary,
} from '../../reducers/documentation/userInputsReducer';
import { createDocumentationItemsMap } from '../../reducers/documentation/createDocumentationItemsMap';

export type FetchDocumentationOptions = {
  /** When false (default), skip the request if this org already has a successful documentation bundle in Redux. */
  force?: boolean;
};

/**
 * One chain of GETs per org: callers await the same promise, and force/non-force never run in parallel
 * (avoids a slow in-flight fetch overwriting fresh data after parse + websocket `force: true`).
 */
const documentationFetchInFlightByOrgId = new Map<string, Promise<void>>();

export const useFetchDocumentation = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { get_salesforceDocumentation, get_salesforceObjectTypes } = useDocumentationApiFacade();

  const fetchDocumentation = useCallback(
    async (crmOrgId: string, options?: FetchDocumentationOptions) => {
      if (!crmOrgId) {
        return;
      }

      const inflight = documentationFetchInFlightByOrgId.get(crmOrgId);
      if (inflight) {
        await inflight;
      }

      if (!options?.force && selectIsOrgFullyFetched(crmOrgId)(store.getState())) {
        return;
      }

      const run = async () => {
        dispatch(beginDocumentationFetch({ crmOrgId }));
        try {
          const [docData, objectTypes] = await Promise.all([
            get_salesforceDocumentation(crmOrgId),
            get_salesforceObjectTypes(crmOrgId),
          ]);

          const parsedData = transformResponse(docData);

          dispatch(setDocumentationData({ crmOrgId, data: parsedData }));
          dispatch(setSweepElements({ crmOrgId, sweepElements: docData.sweepElements }));
          dispatch(setSweepFields({ crmOrgId, sweepFields: docData.sweepFields }));
          dispatch(
            setElementDependencies({ crmOrgId, elementDependencies: docData.elementDependencies }),
          );
          dispatch(
            setSaltoObjectTypes({
              crmOrgId,
              types: objectTypes.map((o) => ({ objectType: o.name, label: o.label })),
            }),
          );

          const nativeItemsMap = createDocumentationItemsMap(
            docData.sweepElements,
            docData.parsedObjects,
          );
          dispatch(addItemsToParserItemsMap({ crmOrgId, newParserItemsMap: nativeItemsMap }));
          dispatch(generateTagsAndUsersSummary({ crmOrgId }));
        } catch (error) {
          telemetry.captureError(error);
          dispatch(failDocumentationFetch({ crmOrgId }));
        }
      };

      const promise = run();
      documentationFetchInFlightByOrgId.set(crmOrgId, promise);

      try {
        await promise;
      } finally {
        documentationFetchInFlightByOrgId.delete(crmOrgId);
      }
    },
    [dispatch, store, get_salesforceDocumentation, get_salesforceObjectTypes],
  );

  return { fetchDocumentation };
};
